import { useState } from 'react';
import { type Category, PALETTE, SUGGESTED_TAXONOMY, plural } from '@quadro/domain';
import { Icon } from '../../components/Icon';
import { Sheet, SheetFooter } from '../../components/Sheet';
import { hud } from '../../components/Hud';
import * as api from '../../data/api';
import { keys, useAction, useTasks } from '../../data/queries';
import { closeSheet } from '../../state/sheets';
import { useUI } from '../../state/ui';
import { useWorkspace } from '../../state/workspace';

type Tab = 'projects' | 'themes' | 'taxonomy';
interface ColorItem { id: string; name: string; color: string; position: number }
const newId = () => crypto.randomUUID();

/** Ajustes da área: projetos, temas e taxonomia (valem para toda a equipe). */
export function SettingsSheet({ tab: initial = 'projects' }: { tab?: Tab }) {
  const ws = useWorkspace();
  const tasks = useTasks().data ?? [];
  const [tab, setTab] = useState<Tab>(initial);
  const [projects, setProjects] = useState<ColorItem[]>(() => ws.settings.projects.length || initial !== 'projects' ? structuredClone(ws.settings.projects) : [{ id: newId(), name: '', color: PALETTE[0], position: 0 }]);
  const [themes, setThemes] = useState<ColorItem[]>(() => ws.settings.themes.length || initial !== 'themes' ? structuredClone(ws.settings.themes) : [{ id: newId(), name: '', color: PALETTE[0], position: 0 }]);
  const [cats, setCats] = useState<Category[]>(() => structuredClone(ws.settings.categories));
  const save = useAction(api.saveTaxonomy, [keys.settings]);

  const usage = (k: 'projects' | 'themes', id: string) => tasks.filter(t => (k === 'projects' ? t.projectId === id : t.themeIds.includes(id))).length;

  function colorList(k: 'projects' | 'themes') {
    const list = k === 'projects' ? projects : themes, setList = k === 'projects' ? setProjects : setThemes;
    const noun = k === 'projects' ? 'projeto' : 'tema';
    const patch = (i: number, p: Partial<ColorItem>) => setList(l => l.map((x, j) => (j === i ? { ...x, ...p } : x)));
    return (
      <>
        <p className="muted aj-intro">{k === 'projects' ? 'Os projetos organizam o quadro. Ao remover um projeto, as tarefas continuam, sem projeto.' : 'Temas são etiquetas transversais: uma tarefa pode ter vários. Use na captura com +nome.'}</p>
        {list.length ? list.map((p, i) => (
          <div className="prow" key={p.id}>
            <div className="sw" role="group" aria-label="Cor">{PALETTE.map(c => <button key={c} type="button" style={{ '--c': c } as React.CSSProperties} aria-pressed={p.color === c} aria-label={`Cor ${c}`} onClick={() => patch(i, { color: c })} />)}</div>
            <input type="text" className="plain" value={p.name} placeholder={`Nome do ${noun}`} maxLength={40} aria-label={`Nome do ${noun}`} autoFocus={!p.name} onChange={e => patch(i, { name: e.target.value })} />
            <span className="muted aj-uso">{plural(usage(k, p.id), 'tarefa', 'tarefas')}</span>
            <button type="button" className="x-btn" aria-label="Remover" onClick={() => setList(l => l.filter((_, j) => j !== i))}><Icon name="x" /></button>
          </div>
        )) : <p className="muted">Nenhum {noun} ainda.</p>}
        <button type="button" className="side-link" style={{ marginTop: 10 }} onClick={() => setList(l => [...l, { id: newId(), name: '', color: PALETTE[l.length % PALETTE.length], position: l.length }])}><Icon name="plus" /> Novo {noun}</button>
      </>
    );
  }

  const patchCat = (i: number, p: Partial<Category>) => setCats(l => l.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const taxonomy = (
    <>
      <p className="muted aj-intro">Uma classificação opcional e padronizada das tarefas, em dois níveis: categoria e subcategoria. Ajuda a filtrar e entender para onde vai o esforço da área.</p>
      {cats.length ? cats.map((c, i) => (
        <div className="tx-cat" key={c.id}>
          <div className="tx-top">
            <input type="text" className="plain" value={c.name} placeholder="Nome da categoria" maxLength={40} aria-label="Nome da categoria" autoFocus={!c.name} onChange={e => patchCat(i, { name: e.target.value })} />
            <span className="muted aj-uso">{plural(tasks.filter(t => t.classification?.categoryId === c.id).length, 'tarefa', 'tarefas')}</span>
            <button type="button" className="x-btn" aria-label="Remover categoria" onClick={() => setCats(l => l.filter((_, j) => j !== i))}><Icon name="x" /></button>
          </div>
          <div className="tx-subs">
            {c.subcategories.map((s, j) => (
              <span className="tx-sub" key={s.id}>{s.name}<button type="button" aria-label={`Remover ${s.name}`} onClick={() => patchCat(i, { subcategories: c.subcategories.filter((_, k) => k !== j) })}><Icon name="x" /></button></span>
            ))}
            <input className="tx-new" placeholder="Nova subcategoria e Enter" aria-label="Nova subcategoria" maxLength={40}
              onKeyDown={e => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const v = (e.target as HTMLInputElement).value.trim(); if (!v) return;
                patchCat(i, { subcategories: [...c.subcategories, { id: newId(), name: v }] });
                (e.target as HTMLInputElement).value = '';
              }} />
          </div>
        </div>
      )) : (
        <div className="tx-vazio"><p>Nenhuma categoria ainda.</p>
          <button type="button" className="btn" onClick={() => setCats(SUGGESTED_TAXONOMY.map(([n, ss], i) => ({ id: newId(), name: n, position: i, subcategories: ss.map(s => ({ id: newId(), name: s })) })))}>Usar modelo sugerido</button>
        </div>
      )}
      <button type="button" className="side-link" style={{ marginTop: 10 }} onClick={() => setCats(l => [...l, { id: newId(), name: '', position: l.length, subcategories: [] }])}><Icon name="plus" /> Nova categoria</button>
    </>
  );

  async function submit() {
    const clean = <T extends { name: string }>(l: T[]) => l.map(x => ({ ...x, name: x.name.trim() })).filter(x => x.name);
    await save(ws.settings, { projects: clean(projects), themes: clean(themes), categories: clean(cats).map(c => ({ ...c, subcategories: clean(c.subcategories) })) });
    const ui = useUI.getState();
    if (ui.scope.project !== 'all' && ui.scope.project !== 'mine' && !projects.some(p => p.id === ui.scope.project && p.name.trim())) ui.setScope({ project: 'all' });
    closeSheet(); hud('Ajustes salvos');
  }

  return (
    <Sheet wide onClose={closeSheet} label="Ajustes da área">
      <div className="sh-body"><h2>Ajustes da área</h2>
        <div className="seg tabs" role="tablist" aria-label="Seções dos ajustes">
          {([['projects', 'Projetos'], ['themes', 'Temas'], ['taxonomy', 'Taxonomia']] as const).map(([k, v]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{v}</button>
          ))}
        </div>
        {tab === 'taxonomy' ? taxonomy : colorList(tab)}
      </div>
      <SheetFooter><span className="muted" style={{ fontSize: 12 }}>Vale para toda a equipe</span><span className="grow" /><button className="btn" onClick={closeSheet}>Cancelar</button><button className="btn primary" onClick={submit}>Salvar</button></SheetFooter>
    </Sheet>
  );
}
